import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    Home, Inbox, Calendar, BookOpen, User, Bell, ArrowLeft, LogOut,
} from "lucide-react";
import { useTutorAuth } from "../../context/TutorAuthContext";

/**
 * V25 Phase 5 — Mobile-First shared layout for Tutor Portal.
 *
 * Pattern:
 *   <TutorMobileLayout title="Dashboard"> ... </TutorMobileLayout>
 *
 * Renders:
 *   - Sticky top header (slim, brand color, optional back button)
 *   - Children (with bottom-nav-safe padding)
 *   - Fixed bottom navigation bar (5 items) — visible only on mobile/small
 *
 * Designed for 320–480px first; gracefully scales to desktop with a narrow
 * centered container (mimicking native app feel even on big screens).
 */
const NAV = [
    { id: "dashboard", label: "Home", to: "/tutor/dashboard", icon: Home },
    { id: "leads", label: "Leads", to: "/tutor/leads", icon: Inbox },
    { id: "demos", label: "Demos", to: "/tutor/demos", icon: Calendar },
    { id: "tuitions", label: "Tuitions", to: "/tutor/tuitions", icon: BookOpen },
    { id: "profile", label: "Me", to: "/tutor/me", icon: User },
];

function isActive(loc, to) {
    if (to === "/tutor/dashboard") return loc.pathname === to;
    return loc.pathname.startsWith(to);
}

export default function TutorMobileLayout({ title, backTo, action, children, hideNav = false }) {
    const navigate = useNavigate();
    const loc = useLocation();
    const { tutor, logout } = useTutorAuth();

    const onLogout = async () => {
        await logout();
        navigate("/tutor/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="tutor-mobile-layout">
            {/* Header */}
            <header className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white sticky top-0 z-40 shadow-sm">
                <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        {backTo ? (
                            <button onClick={() => navigate(backTo)} data-testid="tutor-header-back"
                                className="p-1 -ml-1 rounded-full hover:bg-white/10 active:scale-95 transition">
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                        ) : null}
                        <div className="min-w-0">
                            <h1 className="text-sm font-bold truncate" data-testid="tutor-header-title">{title}</h1>
                            {!backTo && tutor && (
                                <div className="text-[10px] opacity-80 truncate">Hi, {(tutor.name || tutor.email).split(" ")[0]}</div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {action}
                        <Link to="/tutor/notifications" data-testid="tutor-header-notifications"
                            className="p-2 rounded-full hover:bg-white/10 active:scale-95 transition">
                            <Bell className="w-4 h-4" />
                        </Link>
                        <button onClick={onLogout} data-testid="tutor-header-logout"
                            className="p-2 rounded-full hover:bg-white/10 active:scale-95 transition">
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Main */}
            <main className={`flex-1 max-w-md w-full mx-auto px-4 py-4 ${hideNav ? "" : "pb-24"}`}>
                {children}
            </main>

            {/* Bottom nav */}
            {!hideNav && (
                <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] z-40"
                    data-testid="tutor-bottom-nav"
                    style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
                    <div className="max-w-md mx-auto grid grid-cols-5">
                        {NAV.map((n) => {
                            const Icon = n.icon;
                            const active = isActive(loc, n.to);
                            return (
                                <Link key={n.id} to={n.to} data-testid={`tutor-nav-${n.id}`}
                                    className={`flex flex-col items-center justify-center py-2 transition-all relative ${active ? "text-[#1E3A8A]" : "text-slate-400 hover:text-slate-600"}`}>
                                    {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#1E3A8A] rounded-full"></span>}
                                    <Icon className={`w-5 h-5 ${active ? "scale-110" : ""} transition-transform`} />
                                    <span className="text-[10px] font-medium mt-0.5">{n.label}</span>
                                </Link>
                            );
                        })}
                    </div>
                </nav>
            )}
        </div>
    );
}

/** Reusable skeleton card for mobile lists. */
export function MobileSkeletonCard() {
    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 animate-pulse">
            <div className="h-3 bg-slate-200 rounded w-1/3 mb-3"></div>
            <div className="h-4 bg-slate-200 rounded w-2/3 mb-2"></div>
            <div className="h-3 bg-slate-100 rounded w-1/2"></div>
        </div>
    );
}
